import React, { useState, useEffect } from "react";
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import "./styles/global.css";
import "./styles/chat.css";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Recent from "./pages/Recent";
// Premium.tsx removed - now redirects to /pricing
import Pricing from "./pages/Pricing";
import Subscription from "./pages/Subscription";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SubscriptionCancel from "./pages/SubscriptionCancel";
import Profile from "./pages/Profile";
import GetStarted from "./pages/GetStarted";
import ConfirmOtp from "./pages/Confirm";
import Chat from "./pages/Chat";
import Report from "./pages/Report";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import useAIModels from "./hooks/useAIModels";
import { storage } from "./utils/storage";
import { useUserStore } from "./store/userStore";
import SelectModel from "./pages/SelectModel";
import Terms from "./pages/Terms";
import ErrorBoundary from "./components/ErrorBoundary";
import "./utils/apiClient"; // registers axios 401-refresh interceptor (audit C5.5)
import { usePageTracking } from "./hooks/useAnalytics";
import { initializeFirebase } from "./config/firebase";

function App() {
  // Initialize Firebase Analytics on app load
  useEffect(() => {
    initializeFirebase();
  }, []);

  return <AppContent />;
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const fetchProfile = useUserStore((state) => state.fetchProfile);

  // Debug: Log current route
  useEffect(() => {
    console.log('🔴 [App.js] Current pathname:', location.pathname);
  }, [location.pathname]);

  // Track page views with Firebase Analytics
  usePageTracking();

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (!isLoggedIn()) {
      const publicPaths = [
        "/get-started",
        "/terms",
        "/confirm",
        "/policy",
        "/privacy-policy",
        "/pricing",
        "/subscription/success",
        "/subscription/cancel",
      ];
      if (!publicPaths.includes(location.pathname)) {
        navigate("/get-started", { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  const [selectedCard, setSelectedCard] = useState("ChatGPT 3.5");
  const [selectedModel, setSelectedModel] = useState(null);
  const { models } = useAIModels();
  const [isScreenHome, setIsScreenHome] = useState(true);
  const [pageHistory, setPageHistory] = useState(["/"]);

  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 560);
  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 560);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsScreenHome(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Track navigation history
    const currentPath = location.pathname;
    if (currentPath && pageHistory[pageHistory.length - 1] !== currentPath) {
      setPageHistory((prev) => [...prev, currentPath]);
    }
  }, [location]);

  useEffect(() => {
    if (models.length > 0 && !selectedModel) {
      setSelectedModel(models[0]);
    }
  }, [models, selectedModel]);

  const isLoggedIn = () => {
    return !!storage.getJwtToken();
  };

  const redirectIfNotLoggedIn = (path) => {
    if (!isLoggedIn()) {
      navigate("/get-started");
      return true;
    }
    return false;
  };

  const handleNewChat = () => {
    if (redirectIfNotLoggedIn()) return;

    const modelToUse = selectedModel || (models.length > 0 ? models[0] : null);
    if (modelToUse) {
      navigate(`/chat/${modelToUse.id}`);
    } else {
      navigate("/");
    }
  };

  const ProtectedRoute = ({ children }) => {
    useEffect(() => {
      if (!isLoggedIn()) {
        navigate("/get-started");
      }
    }, []);

    return isLoggedIn() ? children : null;
  };

  const getGoBackDestination = () => {
    const path = location.pathname;

    // Find previous page in history
    const currentIndex = pageHistory.lastIndexOf(path);
    if (currentIndex > 0) {
      // Get the previously accessed page from history
      return pageHistory[currentIndex - 1];
    }

    // Fallback if not found in history
    if (path === "/get-started") {
      return "/";
    } else if (path === "/confirm") {
      return "/get-started";
    } else {
      return "/";
    }
  };

  const handleGoBack = () => {
    navigate(getGoBackDestination());
  };

  if (isScreenHome) {
    return (
      <div
        style={{
          backgroundImage: "url('/assets/images/bg-recent.jpeg')",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
          backgroundSize: "cover",
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}>
        <img src="/assets/images/icon-screen-home.svg" alt="logo" />
      </div>
    );
  }

  const BACKGROUND_POSITION_SMALL = "left -358px bottom 300px";
  const BACKGROUND_POSITION_DEFAULT = "center";

  return (
    <ErrorBoundary>
    <Routes>
      <Route
        path="/"
        element={
          isLoggedIn() ? (
            <Home
              render={(handleChatInputFocus) => (
                <Layout
                  showChatInput={location.pathname === "/"}
                  onFocusInput={handleChatInputFocus}
                  footerBackground="rgba(0, 0, 0, 0.2)"
                  backgroundImage="/assets/images/bg_homepage.jpg"
                  backgroundBlur={true}
                  pageType="home"></Layout>
              )}
              setSelectedCard={setSelectedCard}
            />
          ) : (
            <Navigate to="/get-started" replace />
          )
        }
      />
      <Route
        path="/chat/:modelId"
        element={
          <ProtectedRoute>
            <Layout
              showChatInput={false}
              showFooter={false}
              classGetStarted={"class-chat"}
              pageType="chat">
              <Chat />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat/conversation/:documentId"
        element={
          <ProtectedRoute>
            <Layout
              showChatInput={false}
              showFooter={false}
              classGetStarted={"class-chat"}
              pageType="chat">
              <Chat />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recent"
        element={
          <ProtectedRoute>
            <Layout
              headerProps={{
                title: "Recent",
                rightContent: (
                  <div className="cursor-pointer" onClick={handleNewChat}>
                    New Chat
                  </div>
                ),
              }}
              headerBackground="#212226"
              classGetStarted={"class-chat"}
              showFooter={true}
              backgroundPosition={
                isSmallScreen
                  ? BACKGROUND_POSITION_SMALL
                  : BACKGROUND_POSITION_DEFAULT
              }
              pageType="recent">
              <Recent />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/premium"
        element={<Navigate to="/pricing" replace />}
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout
              headerProps={{
                title: "Profile",
                backButton: true,
                onBack: handleGoBack,
              }}
              showFooter={true}
              backgroundImage="/assets/images/bg-profile.jpeg"
              backgroundPosition={
                isSmallScreen
                  ? BACKGROUND_POSITION_SMALL
                  : BACKGROUND_POSITION_DEFAULT
              }
              pageType="other">
              <Profile />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/get-started"
        element={
          <Layout
            headerProps={{
              logoSrc: "/assets/images/icon-screen-home.svg",
              backButton: true,
              onBack: handleGoBack,
            }}
            showFooter={false}
            classGetStarted={
              "class-get-started w-full lg:w-1/2 lg:flex lg:items-center lg:justify-center lg:h-screen"
            }
            imgGetStartedClass={
              "w-full h-full p-4 hidden lg:block lg:object-cover border-40"
            }
            imageGetStarted={"/assets/images/bg_login.png"}
            classProfileLayout="flex flex-col lg:flex-row"
            showNavBar={false}
            pageType="other">
            <GetStarted />
          </Layout>
        }
      />
      <Route
        path="/confirm"
        element={
          <Layout
            headerProps={{
              logoSrc: "/assets/images/icon-screen-home.svg",
              backButton: true,
              onBack: handleGoBack,
            }}
            showFooter={false}
            classGetStarted={
              "class-get-started w-full lg:w-1/2 lg:flex lg:items-center lg:justify-center lg:h-screen"
            }
            imageGetStarted={"/assets/images/bg_login.png"}
            imgGetStartedClass={
              "w-full h-full p-4 hidden lg:block lg:object-cover border-40"
            }
            classProfileLayout="flex flex-col lg:flex-row"
            showNavBar={false}
            pageType="other">
            <ConfirmOtp />
          </Layout>
        }
      />
      <Route
        path="/select-model"
        element={
          <Layout
            headerProps={{
              title: "Models",
              backButton: true,
              onBack: handleGoBack,
            }}
            showFooter={true}
            backgroundImage="/assets/images/bg-profile.jpeg"
            backgroundPosition={
              isSmallScreen
                ? BACKGROUND_POSITION_SMALL
                : BACKGROUND_POSITION_DEFAULT
            }
            pageType="other">
            <SelectModel />
          </Layout>
        }
      />
      <Route
        path="/report"
        element={
          <Layout
            headerProps={{
              title: "Report",
              backButton: true,
              onBack: handleGoBack,
            }}
            showFooter={false}
            backgroundImage="/assets/images/bg-profile.jpeg"
            backgroundPosition={
              isSmallScreen
                ? BACKGROUND_POSITION_SMALL
                : BACKGROUND_POSITION_DEFAULT
            }
            pageType="other">
            <Report />
          </Layout>
        }
      />
      <Route
        path="/terms"
        element={
          <Layout pageType="other" showNavBar={false}>
            <Terms />
          </Layout>
        }
      />
      <Route
        path="/pricing"
        element={<Pricing />}
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <Subscription />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/success"
        element={<SubscriptionSuccess />}
      />
      <Route
        path="/subscription/cancel"
        element={<SubscriptionCancel />}
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
    </Routes>
    </ErrorBoundary>
  );
}

export default App;
